// frontend/src/pages/AssessmentManagement.jsx - Soft delete using assessment_id

import React, { useState, useEffect } from 'react';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    ChartBarIcon,
    EyeIcon,
    DocumentDuplicateIcon,
    FilterIcon,
    CameraIcon,
    VideoCameraIcon
} from '@heroicons/react/outline';
import { getAssessments, createAssessment, updateAssessment, deleteAssessment, getAssessmentStats, getOrganizations } from '../services/adminApi';
import { showSuccess, showError, showInfo } from '../utils/toastUtils';

const AssessmentManagement = () => {
    const [assessments, setAssessments] = useState([]);
    const [filteredAssessments, setFilteredAssessments] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingAssessment, setEditingAssessment] = useState(null);
    const [selectedAssessment, setSelectedAssessment] = useState(null);
    const [stats, setStats] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [userRole, setUserRole] = useState('');
    const [organizationId, setOrganizationId] = useState(null);
    const [selectedOrgFilter, setSelectedOrgFilter] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [formData, setFormData] = useState({
        assessment_id: '',
        name: '',
        client: '',
        assessment_type: 'Behavioural Assessment',
        purpose: '',
        target_population: '',
        version: '1.0',
        status: 'Draft',
        face_verification_enabled: false,
        face_verification_type: 'auto',
        face_photo_count: 3,
        sample_video_url: '',
        sample_video_required: false,
        sample_video_text: ''
    });

    useEffect(() => {
        const userData = localStorage.getItem('adminUser');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                setIsSuperAdmin(user.role === 'super_admin');
                setUserRole(user.role);
                setOrganizationId(user.organization_id);
                setSelectedOrgFilter(user.role === 'super_admin' ? '' : user.organization_id || '');
                console.log('👤 User role:', user.role);
                console.log('🏢 Organization ID:', user.organization_id);
            } catch (e) {
                console.error('Error parsing user data:', e);
            }
        }
        fetchOrganizations();
        fetchAssessments();
    }, []);

    useEffect(() => {
        console.log('📊 [useEffect] formData changed:', formData);
    }, [formData]);

    useEffect(() => {
        if (isSuperAdmin) {
            fetchAssessments();
        }
    }, [showArchived]);

    {
        isSuperAdmin && (
            <div className="flex items-center">
                <input
                    type="checkbox"
                    id="showArchived"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    className="h-4 w-4 text-[#ff9f2f] focus:ring-[#ff9f2f] border-gray-300 rounded"
                />
                <label htmlFor="showArchived" className="ml-2 text-sm text-gray-700">
                    Show Archived
                </label>
            </div>
        )
    }

    const fetchOrganizations = async () => {
        try {
            const response = await getOrganizations();
            setOrganizations(response.data.organizations || []);
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    const fetchAssessments = async () => {
        setLoading(true);
        try {
            const response = await getAssessments({ include_archived: showArchived });
            console.log('📋 Assessments response:', response.data);

            // Filter out archived assessments on client side as well
            const allAssessments = response.data.assessments || [];
            setAssessments(allAssessments);
            applyFilters(allAssessments);
        } catch (error) {
            console.error('Error fetching assessments:', error);
            showError('Failed to load assessments');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = (data) => {
        let filtered = data || assessments;

        if (isSuperAdmin) {
            if (selectedOrgFilter) {
                filtered = filtered.filter(a => a.organization_id === selectedOrgFilter);
            }
        } else {
            if (organizationId) {
                filtered = filtered.filter(a => a.organization_id === organizationId);
            }
        }

        setFilteredAssessments(filtered);
    };

    useEffect(() => {
        applyFilters();
    }, [selectedOrgFilter, assessments]);

    const fetchAssessmentStats = async (assessmentId) => {
        try {
            const response = await getAssessmentStats(assessmentId);
            setStats(response.data.stats);
        } catch (error) {
            console.error(error);
            showError('Failed to load statistics');
        }
    };

    const handleCreate = async () => {
        try {
            const dataToSend = {
                ...formData,
                organization_id: isSuperAdmin ? selectedOrgFilter : organizationId
            };
            await createAssessment(dataToSend);
            setShowModal(false);
            resetFormData();
            showSuccess('Assessment created successfully');
            fetchAssessments();
        } catch (error) {
            console.error(error);
            showError('Failed to create assessment');
        }
    };

    const handleUpdate = async () => {
        try {
            // Use the assessment_id from formData or the editing assessment's id
            const id = formData.assessment_id || editingAssessment?.id;

            console.log('📋 Updating assessment with ID:', id);
            console.log('📋 Form data:', formData);

            if (!id) {
                showError('Assessment ID is required');
                return;
            }

            await updateAssessment(id, formData);
            setShowModal(false);
            setEditingAssessment(null);
            resetFormData();
            showSuccess('Assessment updated successfully');
            fetchAssessments();
        } catch (error) {
            console.error('❌ Update error:', error);
            showError('Failed to update assessment');
        }
    };

    const resetFormData = () => {
        setFormData({
            id: '',
            name: '',
            client: '',
            assessment_type: 'Behavioural Assessment',
            purpose: '',
            target_population: '',
            version: '1.0',
            status: 'Draft',
            face_verification_enabled: false,
            face_verification_type: 'auto',
            face_photo_count: 3,
            sample_video_url: '',
            sample_video_required: false,
            sample_video_text: ''
        });
    };

    // ============================================
    // SOFT DELETE - Archive assessment only
    // ============================================
    const handleDelete = async (assessmentId) => {
        if (!assessmentId) {
            console.error('❌ assessmentId is undefined or null');
            showError('Cannot archive: Assessment ID is missing');
            return;
        }

        // Find the assessment to show its name
        const assessment = assessments.find(a => a.assessment_id === assessmentId);
        const assessmentName = assessment?.name || assessmentId;

        // Show confirmation with the assessment name
        if (!window.confirm(`⚠️ Are you sure you want to archive "${assessmentName}"?\n\nThis will mark the assessment as archived. It will still be available in the system but hidden from active use.`)) {
            return;
        }

        try {
            console.log('🗑️ Archiving assessment with assessment_id:', assessmentId);
            await deleteAssessment(assessmentId);
            showSuccess(`✅ "${assessmentName}" archived successfully`);
            fetchAssessments();
        } catch (error) {
            console.error('❌ Error archiving assessment:', error);
            showError(error.response?.data?.error || 'Failed to archive assessment');
        }
    };

    // ============================================
    // RESTORE ARCHIVED ASSESSMENT
    // ============================================
    const handleRestore = async (assessmentId) => {
        if (!assessmentId) {
            console.error('❌ assessmentId is undefined or null');
            showError('Cannot restore: Assessment ID is missing');
            return;
        }

        const assessment = assessments.find(a => a.assessment_id === assessmentId);
        const assessmentName = assessment?.name || assessmentId;

        if (!window.confirm(`⚠️ Are you sure you want to restore "${assessmentName}"?`)) {
            return;
        }

        try {
            console.log('🔄 Restoring assessment with assessment_id:', assessmentId);
            await updateAssessment(assessmentId, { status: 'Draft' });
            showSuccess(`✅ "${assessmentName}" restored successfully`);
            fetchAssessments();
        } catch (error) {
            console.error('❌ Error restoring assessment:', error);
            showError(error.response?.data?.error || 'Failed to restore assessment');
        }
    };

    const handleEdit = (assessment) => {
        // Use the id directly (since assessment_id may not exist)
        const assessmentId = assessment.id || assessment.assessment_id;

        console.log('📋 Editing assessment:', assessment);
        console.log('📋 Assessment ID:', assessmentId);

        setEditingAssessment(assessment);
        setFormData({
            id: assessmentId || '',
            name: assessment.name || '',
            client: assessment.client || '',
            assessment_type: assessment.assessment_type || 'Behavioural Assessment',
            purpose: assessment.purpose || '',
            target_population: assessment.target_population || '',
            version: assessment.version || '1.0',
            status: assessment.status || 'Draft',
            face_verification_enabled: assessment.face_verification_enabled || false,
            face_verification_type: assessment.face_verification_type || 'auto',
            face_photo_count: assessment.face_photo_count || 3,
            sample_video_url: assessment.sample_video_url || '',
            sample_video_required: assessment.sample_video_required || false,
            sample_video_text: assessment.sample_video_text || ''
        });
        setShowModal(true);
    };

    const handleViewStats = async (assessment) => {
        setSelectedAssessment(assessment);
        await fetchAssessmentStats(assessment.assessment_id);
    };

    const getStatusBadge = (status) => {
        const colors = {
            Draft: 'bg-gray-100 text-gray-800',
            Active: 'bg-green-100 text-green-800',
            Archived: 'bg-red-100 text-red-800',
            Under_Review: 'bg-yellow-100 text-yellow-800'
        };
        return (
            <span className={`px-2 py-1 text-xs rounded-full ${colors[status] || 'bg-gray-100'}`}>
                {status}
            </span>
        );
    };

    const getOrganizationName = (orgId) => {
        const org = organizations.find(o => o.id === orgId);
        return org ? org.name : orgId;
    };

    const renderModal = () => {
        if (!showModal || !isSuperAdmin) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="p-6 border-b sticky top-0 bg-white z-10">
                        <h2 className="text-xl font-bold">
                            {editingAssessment ? 'Edit Assessment' : 'New Assessment'}
                        </h2>
                    </div>

                    <div className="p-6 space-y-4">
                        {/* Basic Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Assessment ID *
                                </label>
                                <input
                                    type="text"
                                    value={formData.assessment_id}
                                    onChange={(e) => setFormData({ ...formData, assessment_id: e.target.value.toUpperCase() })}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="e.g., MIPS-00001"
                                    disabled={!!editingAssessment}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Assessment Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="e.g., Personality Style Index"
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Client / Project
                                </label>
                                <input
                                    type="text"
                                    value={formData.client}
                                    onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="e.g., PsychCatalyst"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Assessment Type
                                </label>
                                <select
                                    value={formData.assessment_type}
                                    onChange={(e) => setFormData({ ...formData, assessment_type: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                >
                                    <option>Behavioural Assessment</option>
                                    <option>Cognitive Assessment</option>
                                    <option>Clinical Assessment</option>
                                    <option>Skills Assessment</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Purpose
                            </label>
                            <textarea
                                value={formData.purpose}
                                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                                className="w-full border rounded-lg px-3 py-2"
                                rows="2"
                                placeholder="e.g., Selection & Leadership Development"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Target Population
                                </label>
                                <input
                                    type="text"
                                    value={formData.target_population}
                                    onChange={(e) => setFormData({ ...formData, target_population: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="e.g., Early Career Professionals"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Version
                                </label>
                                <input
                                    type="text"
                                    value={formData.version}
                                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="1.0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Status
                                </label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                >
                                    <option>Draft</option>
                                    <option>Active</option>
                                    <option>Under_Review</option>
                                    <option>Archived</option>
                                </select>
                            </div>
                        </div>

                        {isSuperAdmin && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Organization
                                </label>
                                <select
                                    value={formData.organization_id || selectedOrgFilter || ''}
                                    onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                >
                                    <option value="">Select Organization</option>
                                    {organizations.map((org) => (
                                        <option key={org.id} value={org.id}>
                                            {org.name} ({org.id})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Face Verification Settings */}
                        <div className="border-t border-gray-200 pt-4 mt-4">
                            <h3 className="text-md font-semibold text-gray-700 mb-3 flex items-center">
                                <CameraIcon className="h-5 w-5 mr-2 text-[#063b56]" />
                                Face Verification Settings
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="face_verification_enabled"
                                        checked={formData.face_verification_enabled}
                                        onChange={(e) => setFormData({ ...formData, face_verification_enabled: e.target.checked })}
                                        className="h-4 w-4 text-[#ff9f2f] focus:ring-[#ff9f2f] border-gray-300 rounded"
                                    />
                                    <label htmlFor="face_verification_enabled" className="ml-2 text-sm text-gray-700">
                                        Enable Face Verification
                                    </label>
                                </div>

                                {formData.face_verification_enabled && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Verification Type
                                            </label>
                                            <select
                                                value={formData.face_verification_type}
                                                onChange={(e) => setFormData({ ...formData, face_verification_type: e.target.value })}
                                                className="w-full border rounded-lg px-3 py-2"
                                            >
                                                <option value="auto">Automatic (Background)</option>
                                                <option value="manual">Manual (User clicks)</option>
                                                <option value="passive">Passive (No active capture)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Number of Photos
                                            </label>
                                            <select
                                                value={formData.face_photo_count}
                                                onChange={(e) => setFormData({ ...formData, face_photo_count: parseInt(e.target.value) })}
                                                className="w-full border rounded-lg px-3 py-2"
                                            >
                                                <option value="1">1 Photo</option>
                                                <option value="2">2 Photos</option>
                                                <option value="3">3 Photos</option>
                                            </select>
                                        </div>
                                    </>
                                )}
                            </div>

                            {formData.face_verification_enabled && (
                                <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                    <p className="text-xs text-blue-600">
                                        <strong>Note:</strong> Face verification will capture photos of the candidate
                                        during the assessment. Photos are stored securely and can be viewed in the candidate dashboard.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Sample Video Settings */}
                        <div className="border-t border-gray-200 pt-4 mt-4">
                            <h3 className="text-md font-semibold text-gray-700 mb-3 flex items-center">
                                <VideoCameraIcon className="h-5 w-5 mr-2 text-[#063b56]" />
                                Sample Video / Instructions
                            </h3>

                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Sample Video URL
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.sample_video_url}
                                        onChange={(e) => setFormData({ ...formData, sample_video_url: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                        placeholder="https://drive.google.com/file/d/.../preview"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">
                                        Enter a Google Drive video URL or any video embed URL
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Instructions Text
                                    </label>
                                    <textarea
                                        value={formData.sample_video_text}
                                        onChange={(e) => setFormData({ ...formData, sample_video_text: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                        rows="2"
                                        placeholder="Watch this video to understand the assessment format..."
                                    />
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="sample_video_required"
                                        checked={formData.sample_video_required}
                                        onChange={(e) => setFormData({ ...formData, sample_video_required: e.target.checked })}
                                        className="h-4 w-4 text-[#ff9f2f] focus:ring-[#ff9f2f] border-gray-300 rounded"
                                    />
                                    <label htmlFor="sample_video_required" className="ml-2 text-sm text-gray-700">
                                        Require watching sample video before starting assessment
                                    </label>
                                </div>
                            </div>

                            {formData.sample_video_url && (
                                <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                    <p className="text-xs text-gray-500">
                                        <strong>Preview:</strong>
                                        <a
                                            href={formData.sample_video_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="ml-2 text-[#ff9f2f] hover:underline"
                                        >
                                            {formData.sample_video_url}
                                        </a>
                                    </p>
                                    {formData.sample_video_text && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            <strong>Instructions:</strong> {formData.sample_video_text}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3 sticky bottom-0 bg-white border-t">
                        <button
                            onClick={() => {
                                setShowModal(false);
                                setEditingAssessment(null);
                                resetFormData();
                            }}
                            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={editingAssessment ? handleUpdate : handleCreate}
                            className="px-4 py-2 bg-[#0a4a6a] text-white rounded-lg hover:bg-[#063b56]"
                        >
                            {editingAssessment ? 'Update' : 'Create'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0a4a6a]"></div>
            </div>
        );
    }

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Assessment Management</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {!isSuperAdmin && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                Organization Admin - Viewing assigned assessments only
                            </span>
                        )}
                        {isSuperAdmin && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                Super Admin - Full access
                            </span>
                        )}
                    </p>
                </div>
                <div className="flex flex-wrap gap-3">
                    {isSuperAdmin && (
                        <div className="flex items-center space-x-2">
                            <FilterIcon className="h-5 w-5 text-gray-400" />
                            <select
                                value={selectedOrgFilter}
                                onChange={(e) => setSelectedOrgFilter(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#ff9f2f] focus:border-[#ff9f2f] min-w-[180px]"
                            >
                                <option value="">All Organizations</option>
                                {organizations.map((org) => (
                                    <option key={org.id} value={org.id}>
                                        {org.name} ({org.id})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {isSuperAdmin && (
                        <button
                            onClick={() => {
                                setEditingAssessment(null);
                                resetFormData();
                                setShowModal(true);
                            }}
                            className="bg-[#0a4a6a] text-white px-4 py-2 rounded-lg hover:bg-[#063b56] flex items-center"
                        >
                            <PlusIcon className="h-5 w-5 mr-2" />
                            New Assessment
                        </button>
                    )}
                </div>
            </div>

            {/* Organization Info for Org Admin */}
            {!isSuperAdmin && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-700">
                        <strong>Organization:</strong> {organizationId || 'N/A'}
                        <span className="ml-3 text-xs text-blue-500">
                            Showing assessments assigned to your organization only
                        </span>
                    </p>
                </div>
            )}

            {/* Filter Info for Super Admin */}
            {isSuperAdmin && selectedOrgFilter && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600">
                        <strong>Filtering by:</strong> {getOrganizationName(selectedOrgFilter)}
                        <span className="ml-3 text-xs text-gray-400">
                            {filteredAssessments.length} assessments found
                        </span>
                    </p>
                </div>
            )}

            {isSuperAdmin && !selectedOrgFilter && (
                <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700">
                        <strong>Showing All Organizations</strong>
                        <span className="ml-3 text-xs text-green-600">
                            {filteredAssessments.length} total assessments
                        </span>
                    </p>
                </div>
            )}

            {/* Stats Modal */}
            {selectedAssessment && stats && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-bold">{selectedAssessment.name} - Statistics</h2>
                                <button
                                    onClick={() => setSelectedAssessment(null)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div className="bg-blue-50 p-4 rounded-lg text-center">
                                    <div className="text-2xl font-bold text-blue-600">{stats.totalSessions}</div>
                                    <div className="text-sm text-gray-600">Total Sessions</div>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg text-center">
                                    <div className="text-2xl font-bold text-green-600">{stats.completedSessions}</div>
                                    <div className="text-sm text-gray-600">Completed</div>
                                </div>
                                <div className="bg-purple-50 p-4 rounded-lg text-center">
                                    <div className="text-2xl font-bold text-purple-600">{Math.round(stats.completionRate)}%</div>
                                    <div className="text-sm text-gray-600">Completion Rate</div>
                                </div>
                                <div className="bg-orange-50 p-4 rounded-lg text-center">
                                    <div className="text-2xl font-bold text-orange-600">
                                        {Math.floor(stats.averageCompletionTimeSeconds / 60)} min
                                    </div>
                                    <div className="text-sm text-gray-600">Avg. Time</div>
                                </div>
                            </div>

                            {stats.completionTrend && stats.completionTrend.length > 0 && (
                                <div>
                                    <h3 className="font-semibold mb-3">Recent Activity (Last 30 Days)</h3>
                                    <div className="space-y-2">
                                        {stats.completionTrend.slice(0, 7).map((day, idx) => (
                                            <div key={idx} className="flex justify-between items-center">
                                                <span className="text-sm text-gray-600">
                                                    {new Date(day.date).toLocaleDateString()}
                                                </span>
                                                <div className="flex-1 mx-4">
                                                    <div className="bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className="bg-green-500 rounded-full h-2"
                                                            style={{ width: `${Math.min(100, (day.count / 10) * 100)}%` }}
                                                        />
                                                    </div>
                                                </div>
                                                <span className="text-sm font-medium">{day.count} completions</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Create/Edit Modal */}
            {renderModal()}

            {/* Assessments Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Version</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Face Verify</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sample Video</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="11" className="px-6 py-12 text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                                    </td>
                                </tr>
                            ) : filteredAssessments.length === 0 ? (
                                <tr>
                                    <td colSpan="11" className="px-6 py-12 text-center text-gray-400">
                                        {!isSuperAdmin ?
                                            'No assessments assigned to your organization yet.' :
                                            selectedOrgFilter ?
                                                'No assessments found for the selected organization.' :
                                                'No assessments found. Click "New Assessment" to create one.'}
                                    </td>
                                </tr>
                            ) : (
                                filteredAssessments.map(assessment => (
                                    <tr key={assessment.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 font-mono text-sm">{assessment.assessment_id}</td>
                                        <td className="px-6 py-4 font-medium">{assessment.name}</td>
                                        <td className="px-6 py-4 text-gray-600">{assessment.client || '-'}</td>
                                        <td className="px-6 py-4 text-gray-600">{assessment.assessment_type || '-'}</td>
                                        <td className="px-6 py-4 text-gray-600">v{assessment.version}</td>
                                        <td className="px-6 py-4">{getStatusBadge(assessment.status)}</td>
                                        <td className="px-6 py-4">
                                            {assessment.face_verification_enabled ? (
                                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                                    ✅ Yes
                                                </span>
                                            ) : (
                                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                                    No
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {assessment.sample_video_url ? (
                                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                                    ✅ {assessment.sample_video_required ? '(Required)' : '(Optional)'}
                                                </span>
                                            ) : (
                                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                                    No
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {new Date(assessment.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-xs text-gray-500">
                                            {assessment.organization_id ? getOrganizationName(assessment.organization_id) : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end space-x-2">
                                                <button
                                                    onClick={() => handleViewStats(assessment)}
                                                    className="p-1 text-blue-600 hover:text-blue-800"
                                                    title="View Statistics"
                                                >
                                                    <ChartBarIcon className="h-5 w-5" />
                                                </button>

                                                {isSuperAdmin && assessment.status !== 'Archived' && (
                                                    <button
                                                        onClick={() => handleEdit(assessment)}
                                                        className="p-1 text-green-600 hover:text-green-800"
                                                        title="Edit"
                                                    >
                                                        <PencilIcon className="h-5 w-5" />
                                                    </button>
                                                )}

                                                {isSuperAdmin && assessment.status !== 'Archived' && (
                                                    <button
                                                        onClick={() => handleDelete(assessment.assessment_id)}
                                                        className="p-1 text-red-600 hover:text-red-800"
                                                        title="Archive"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                )}

                                                {isSuperAdmin && assessment.status === 'Archived' && (
                                                    <button
                                                        onClick={() => handleRestore(assessment.assessment_id)}
                                                        className="p-1 text-blue-600 hover:text-blue-800"
                                                        title="Restore"
                                                    >
                                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.418 0V4h-.582m-15.418 5v11a2 2 0 002 2h14a2 2 0 002-2V9m-15.418 0h15.418" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11v6m-3-3h6" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Footer Stats */}
            <div className="mt-4 text-center text-xs text-gray-400">
                {!isSuperAdmin && (
                    <p>You are viewing assessments assigned to your organization only.</p>
                )}
                {isSuperAdmin && selectedOrgFilter && (
                    <p>Showing assessments for: {getOrganizationName(selectedOrgFilter)}</p>
                )}
                {isSuperAdmin && !selectedOrgFilter && (
                    <p>Showing all assessments across all organizations</p>
                )}
                <p>Total: {filteredAssessments.length} assessments</p>
            </div>
        </div>
    );
};

export default AssessmentManagement;